import { z } from 'zod';
import type { DeviceInstanceId, VmId } from '../domain/ids';
import type { SapInstance, SapInstanceWritePayload } from '../domain/sapInstance';
import {
  softwareAssociationRepository,
  type SoftwareAssociationDto,
} from './softwareAssociation.repository';

// T39 (fusionada con T40, ver softwareAssociation.repository.ts para el detalle del contrato
// unificado): v1 no tiene `/vm-sap-instances` — las instancias SAP son
// `software-associations` con `category: 'sap_instance'` bajo
// `/device-instances/{deviceInstanceId}/virtual-machines/{vmId}/software-associations`.
// `deviceInstanceId` se añade como PRIMER parámetro a `getAll`/`create`/`update`/`remove` (antes no
// lo recibía este repositorio) — propagado desde `useVmSapInstances.ts`, que a su vez lo recibe de
// `VmSapTab.tsx` (nuevo prop) y de `VmDetailPage.tsx` (ya lo tenía como `instanceId`).
//
// `sapProductId` (dominio) ahora es `string` (antes `number`): mapea 1:1 a `catalogSelectionId` de
// v1, que es siempre string (igual que `CatalogOption.id` en `catalogOptions.repository.ts`).
//
// GAP DE DATOS no bloqueante: `sapProductName` (dominio) solo tiene como fuente
// `context.catalogSelectionLabel` (v1) — un único string, no el objeto de catálogo completo que
// enriquecía el legado. El resto de campos (`sid`/`instanceNumber`/`instanceStack`/`architecture`/
// `dbType`/`dbName`/`systemDescription`/`hardwareKey`/`version`/`supportPackage`/
// `enhancementPackage`) viven en `attributes` (bolsa libre), con el mismo nombre de campo
// (snake_case) que el legado ya usaba — se valida con zod al leer, igual que cualquier otro DTO de
// `infrastructure/`. `sapEndOfMainstream` (solo lectura, ausente del payload de escritura tanto en
// legado como aquí) también vive en `attributes`.
//
// `update` no tiene endpoint de detalle en v1 (solo listado) — se relee `getAll` filtrado por
// `category` y se busca por `id` para obtener el `version` de concurrencia optimista que exige
// `PATCH .../software-associations/{id}` (mismo principio de "releer antes de actualizar" que
// T31/T38, adaptado a la ausencia de GET individual).

/** `SapInstance.id` es `string | number` (domain/sapInstance.ts) — el repositorio coerciona a
 * `string` al hablar con `softwareAssociationRepository` (el id de asociación de v1 es siempre
 * string), preservando la firma pública existente para no romper `application/`. */
type SapInstanceId = string | number;

const SapAttributesSchema = z.object({
  sid: z.string(),
  instance_number: z.string(),
  instance_stack: z.string().nullish(),
  architecture: z.string().nullish(),
  db_type: z.string().nullish(),
  db_name: z.string().nullish(),
  system_description: z.string().nullish(),
  hardware_key: z.string().nullish(),
  version: z.string().nullish(),
  support_package: z.string().nullish(),
  enhancement_package: z.string().nullish(),
  sap_end_of_mainstream: z.string().nullish(),
});

export interface SapInstanceListParams {
  vmId?: VmId;
}

function toDomain(dto: SoftwareAssociationDto): SapInstance {
  const a = SapAttributesSchema.parse(dto.attributes);
  return {
    id: dto.id,
    vmId: dto.virtual_machine_id as VmId,
    sapProductId: dto.catalog_selection_id,
    sapProductName: dto.context?.catalog_selection_label ?? undefined,
    sid: a.sid,
    instanceNumber: a.instance_number,
    instanceStack: a.instance_stack ?? undefined,
    architecture: a.architecture ?? undefined,
    dbType: a.db_type ?? undefined,
    dbName: a.db_name ?? undefined,
    systemDescription: a.system_description ?? undefined,
    hardwareKey: a.hardware_key ?? undefined,
    version: a.version ?? undefined,
    supportPackage: a.support_package ?? undefined,
    enhancementPackage: a.enhancement_package ?? undefined,
    sapEndOfMainstream: a.sap_end_of_mainstream ?? undefined,
  };
}

function toAttributes(payload: SapInstanceWritePayload): Record<string, unknown> {
  return {
    sid: payload.sid,
    instanceNumber: payload.instanceNumber,
    instanceStack: payload.instanceStack,
    architecture: payload.architecture,
    dbType: payload.dbType,
    dbName: payload.dbName,
    systemDescription: payload.systemDescription,
    hardwareKey: payload.hardwareKey,
    version: payload.version,
    supportPackage: payload.supportPackage,
    enhancementPackage: payload.enhancementPackage,
  };
}

export const sapInstanceRepository = {
  async getAll(
    deviceInstanceId: DeviceInstanceId,
    params?: SapInstanceListParams
  ): Promise<SapInstance[]> {
    if (params?.vmId === undefined) return [];
    const all = await softwareAssociationRepository.getAll(
      deviceInstanceId,
      params.vmId,
      'sap_instance'
    );
    return all.map(toDomain);
  },

  async create(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    payload: SapInstanceWritePayload
  ): Promise<SapInstance> {
    const dto = await softwareAssociationRepository.create(deviceInstanceId, vmId, {
      category: 'sap_instance',
      catalogSelectionId: payload.sapProductId,
      attributes: toAttributes(payload),
    });
    return toDomain(dto);
  },

  async update(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    id: SapInstanceId,
    payload: SapInstanceWritePayload
  ): Promise<SapInstance> {
    const associationId = String(id);
    const current = await softwareAssociationRepository.getAll(
      deviceInstanceId,
      vmId,
      'sap_instance'
    );
    const existing = current.find((a) => a.id === associationId);
    const dto = await softwareAssociationRepository.update(deviceInstanceId, vmId, associationId, {
      version: existing?.version ?? 1,
      attributes: toAttributes(payload),
    });
    return toDomain(dto);
  },

  async remove(deviceInstanceId: DeviceInstanceId, vmId: VmId, id: SapInstanceId): Promise<void> {
    await softwareAssociationRepository.remove(deviceInstanceId, vmId, String(id));
  },
};
